import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import "./SalesPages.css";

function Subscribers() {
  const navigate = useNavigate();

  const [subscribers, setSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscribers();
  }, []);

  const formatStatusLabel = (value) => {
    if (!value) return "—";

    return String(value)
      .trim()
      .toLowerCase()
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map(
        (word) =>
          word.charAt(0).toUpperCase() +
          word.slice(1)
      )
      .join(" ");
  };

  const getAccountTypeLabel = (subscriber) => {
    if (
      subscriber.is_trial === 1 ||
      subscriber.is_trial === true
    ) {
      return "Trial";
    }

    return "Subscriber";
  };

  const getSubscriptionStatusValue = (subscriber) => {
    if (
      subscriber.is_trial === 1 ||
      subscriber.is_trial === true
    ) {
      return "trial";
    }

    return subscriber.subscription_status || "";
  };

  const getSubscriptionStatusLabel = (subscriber) => {
    return formatStatusLabel(
      getSubscriptionStatusValue(subscriber)
    );
  };

  const getBillingStatusLabel = (subscriber) => {
    return formatStatusLabel(
      subscriber.billing_status
    );
  };

  const getRenewalOrTrialEndLabel = (subscriber) => {
    if (
      subscriber.is_trial === 1 ||
      subscriber.is_trial === true
    ) {
      return subscriber.trial_ends_at
        ? String(subscriber.trial_ends_at).slice(0, 10)
        : "";
    }

    return subscriber.renewal_date
      ? String(subscriber.renewal_date).slice(0, 10)
      : "";
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, "-")}`;

  const filteredSubscribers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    if (!term) return subscribers;

    return subscribers.filter((subscriber) => {
      const subscriptionStatusValue =
        getSubscriptionStatusValue(subscriber);

      const searchableValues = [
        subscriber.business_name,
        subscriber.primary_contact_name,
        subscriber.email,
        subscriber.phone,
        subscriber.plan_name,
        subscriber.subscription_plan_name,
        subscriptionStatusValue,
        formatStatusLabel(subscriptionStatusValue),
        subscriber.billing_status,
        formatStatusLabel(subscriber.billing_status),
        subscriber.provisioning_status,
        formatStatusLabel(
          subscriber.provisioning_status
        ),
        subscriber.subdomain,
        subscriber.full_domain,
        getAccountTypeLabel(subscriber)
      ];

      return searchableValues
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(term)
        );
    });
  }, [searchTerm, subscribers]);

  const loadSubscribers = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/subscribers`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to load subscribers");
      }

      const data = await res.json();

      setSubscribers(
        Array.isArray(data) ? data : []
      );
    } catch (err) {
      console.error(
        "Load subscribers error:",
        err.message
      );

      setSubscribers([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">
              Agent Book CRM
            </p>

            <h1 className="sales-title">
              Subscribers
            </h1>

            <p className="sales-subtitle">
              Manage standard subscribers and trial
              accounts in one shared workflow.
            </p>
          </div>

          <div className="sales-header-actions">
            <button
              type="button"
              className="sales-primary-btn"
              onClick={() =>
                navigate("/new-subscriber")
              }
            >
              + New Subscriber
            </button>
          </div>
        </div>

        <div className="sales-toolbar-card">
          <input
            type="text"
            placeholder="Search subscribers..."
            value={searchTerm}
            onChange={(event) =>
              setSearchTerm(event.target.value)
            }
            className="sales-search-input"
          />
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>Subscriber Accounts</h2>

              <p>
                {filteredSubscribers.length} account
                {filteredSubscribers.length === 1
                  ? ""
                  : "s"}
              </p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table">
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Type</th>
                  <th>Primary Contact</th>
                  <th>Plan</th>
                  <th>Subscription Status</th>
                  <th>Billing Status</th>
                  <th>Renewal / Trial End</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="sales-empty-cell"
                    >
                      Loading subscribers...
                    </td>
                  </tr>
                ) : filteredSubscribers.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="sales-empty-cell"
                    >
                      No subscribers found.
                    </td>
                  </tr>
                ) : (
                  filteredSubscribers.map(
                    (subscriber) => {
                      const accountTypeLabel =
                        getAccountTypeLabel(
                          subscriber
                        );

                      const subscriptionStatusValue =
                        getSubscriptionStatusValue(
                          subscriber
                        );

                      const subscriptionStatusLabel =
                        getSubscriptionStatusLabel(
                          subscriber
                        );

                      const billingStatusLabel =
                        getBillingStatusLabel(
                          subscriber
                        );

                      return (
                        <tr
                          key={subscriber.id}
                          className="sales-click-row"
                          onClick={() =>
                            navigate(
                              `/subscriber-profile/${subscriber.id}`
                            )
                          }
                        >
                          <td>
                            <div className="sales-primary-cell">
                              <div className="sales-name">
                                {subscriber.business_name ||
                                  ""}
                              </div>

                              <div className="sales-secondary">
                                {subscriber.email || ""}
                              </div>
                            </div>
                          </td>

                          <td>
                            <span
                              className={statusClass(
                                accountTypeLabel
                              )}
                            >
                              {accountTypeLabel}
                            </span>
                          </td>

                          <td>
                            <div className="sales-primary-cell">
                              <div className="sales-name">
                                {subscriber.primary_contact_name ||
                                  "—"}
                              </div>

                              <div className="sales-secondary">
                                {subscriber.phone || ""}
                              </div>
                            </div>
                          </td>

                          <td>
                            {subscriber.plan_name ||
                              subscriber.subscription_plan_name ||
                              "—"}
                          </td>

                          <td>
                            <span
                              className={statusClass(
                                subscriptionStatusValue
                              )}
                            >
                              {subscriptionStatusLabel}
                            </span>
                          </td>

                          <td>
                            <span
                              className={statusClass(
                                subscriber.billing_status
                              )}
                            >
                              {billingStatusLabel}
                            </span>
                          </td>

                          <td>
                            {getRenewalOrTrialEndLabel(
                              subscriber
                            )}
                          </td>
                        </tr>
                      );
                    }
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

export default Subscribers;